/**
 * Utilitaires pour mapper les données des enregistrements Coran depuis le backend
 * vers le format frontend
 */

import { GLOBAL_UTILS } from '../styles/globalStyles';

/**
 * Extrait repeatNewHifz depuis un enregistrement quran_records
 * 
 * Cette fonction retourne undefined si la valeur n'existe pas (pas de fallback vers quran_next_session).
 * 
 * Note: 0 est considéré comme une valeur valide, seul null/undefined est considéré comme absent.
 * 
 * @param record - Enregistrement depuis quran_records (format camelCase)
 * @returns repeatNewHifz depuis quran_records, ou undefined si absent
 */
export function extractRepeatNewHifzFromRecord(record: any): number | undefined {
  // Si absent ou null dans quran_records, on laisse undefined (pas de fallback vers quran_next_session)
  // Note: 0 est considéré comme une valeur valide, seul null/undefined est considéré comme absent
  if (record.repeatNewHifz != null) {
    return record.repeatNewHifz;
  }
  return undefined;
}

/**
 * Mappe un enregistrement Coran depuis le backend vers le format frontend
 * Centralise la logique de mapping pour éviter la duplication
 * 
 * @param record - Enregistrement depuis le backend
 * @param nextSession - Session suivante depuis quran_next_session (optionnel)
 * @returns Enregistrement mappé au format frontend
 */
export function mapQuranRecordFromBackend(record: any, nextSession?: any | null) {
  const studentId = record.student_id || record.studentId;
  
  return {
    ...record,
    id: record.id,
    studentId: studentId,
    totalHizb: parseFloat(record.total_hizb) || parseFloat(record.totalHizb) || 0,
    // ✅ آخر جلسة (Last Session) - depuis quran_records
    newHifzTxt: GLOBAL_UTILS.cleanText(record.newHifzTxt || record.new_memorization || ''),
    oldHifzMurajahTxt: GLOBAL_UTILS.cleanText(record.oldHifzMurajahTxt || record.review || ''),
    newHifzMurajahText: GLOBAL_UTILS.cleanText(record.newHifzMurajahText || ''),
    // ✅ الجلسة القادمة (Next Session) - depuis quran_next_session
    nextHalaqaNewHifzText: nextSession ? GLOBAL_UTILS.cleanText(nextSession.nextHalaqaNewHifzText || '') : '',
    nextHalaqaOldHifzMurajahText: nextSession ? GLOBAL_UTILS.cleanText(nextSession.nextHalaqaOldHifzMurajahText || '') : '',
    nextHalaqaNewHifzMurajahText: nextSession ? GLOBAL_UTILS.cleanText(nextSession.nextHalaqaNewHifzMurajahText || '') : '',
    nextHalaqaNewHifzRange: nextSession ? (nextSession.nextHalaqaNewHifzRange || null) : null,
    nextHalaqaOldHifzMurajahRange: nextSession ? (nextSession.nextHalaqaOldHifzMurajahRange || null) : null,
    nextHalaqaNewHifzMurajahRange: nextSession ? (nextSession.nextHalaqaNewHifzMurajahRange || null) : null,
    note: GLOBAL_UTILS.cleanText((record.note || '').trim()),
    date: record.modifiedDate || record.modified_date || record.date || record.createdDate || record.created_date || '',
    modifiedDate: record.modifiedDate || record.modified_date || record.date || record.createdDate || record.created_date || '',
    createdDate: record.createdDate || record.created_date || record.modifiedDate || record.modified_date || record.date || '',
    studentName: GLOBAL_UTILS.cleanText(record.student_name || record.studentName || ''),
    // ✅ Évaluations - PAS de valeurs par défaut, on utilise les valeurs du backend (ou undefined)
    evalNewHifz: record.evalNewHifz,
    evalOldHifzMurajah: record.evalOldHifzMurajah,
    evalNewHifzMurajah: record.evalNewHifzMurajah,
    // ✅ Compteur de répétitions - UNIQUEMENT depuis quran_records
    // ⚠️ IMPORTANT: repeatNewHifz dans quran_records = "تكرار آخر حفظ" (répétitions de la dernière session)
    // ⚠️ repeatNewHifz dans quran_next_session = "تكرار الحفظ الجديد" (répétitions pour la prochaine session)
    // Ces deux valeurs sont différentes et ne doivent pas être mélangées
    repeatNewHifz: extractRepeatNewHifzFromRecord(record),
    // ✅ NOUVEAU - Compteur de répétitions pour la session suivante depuis quran_next_session
    nextSessionRepeatNewHifz: nextSession ? (nextSession.repeatNewHifz ?? 0) : 0
  };
}

